import type { NotificationsResponse } from '@whaletail/shared';
import { subscribePush, unsubscribePush } from './api.ts';

/**
 * Web Push on this device, for nearby-whale alerts (server side: notify.ts).
 *
 * The push itself is shown by the service worker (src/sw.ts), so alerts
 * arrive with the app closed. iPhones and iPads only support this for a web app added to
 * the Home Screen and opened from there (iOS 16.4+), hence 'ios-needs-install'.
 */
export type PushSupport = 'unsupported' | 'ios-needs-install' | 'denied' | 'default' | 'granted';

const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  const capable = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!capable) return isIos() && !isStandalone() ? 'ios-needs-install' : 'unsupported';
  return Notification.permission;
}

/**
 * The service worker registration, or null if none becomes active soon. The
 * Vite dev server (npm run dev:web) registers no worker, and without this
 * timeout `ready` would wait forever.
 */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
  ]);
}

/** This device's current push subscription, if it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'granted') return null;
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

const sameBytes = (a: ArrayBuffer | null, b: Uint8Array) =>
  a !== null && a.byteLength === b.byteLength && new Uint8Array(a).every((v, i) => v === b[i]);

/** A rough, human label for the device list — never used for anything but display. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (isIos()) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'Mac';
  return 'Browser';
}

/**
 * Ask for permission (must run from a click — iOS refuses otherwise), subscribe
 * with the server's VAPID key, and register the device with the server.
 * Throws an Error with a message fit to show when it cannot.
 */
export async function enableOnThisDevice(vapidPublicKey: string): Promise<NotificationsResponse> {
  const support = pushSupport();
  if (support === 'unsupported') throw new Error('This browser cannot receive notifications.');
  if (support === 'ios-needs-install') throw new Error('Add WhaleTail to your Home Screen first, then open it from there.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notifications are blocked for this site. Allow them in your browser settings, then try again.');
  }

  const reg = await registration();
  if (!reg) throw new Error('The app is not installed as an offline web app here yet. Reload the page and try again.');

  const key = base64UrlToBytes(vapidPublicKey);
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with a different server key (the keys were rotated)
  // can never be delivered to; replace it.
  if (sub && !sameBytes(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('The browser returned an incomplete subscription.');
  return subscribePush({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, label: deviceLabel() });
}

// ---------------------------------------------------------------------------
// Remembering who turned alerts on here, across sign-out and sign-in
// ---------------------------------------------------------------------------

/**
 * Alerts are a setting per account, per device: every account that turned
 * alerts on in this browser is remembered here. Signing out only pauses them
 * (the server stops sending); signing back in resumes them for that account
 * without redoing "Turn on here". Someone else signing in on the same device
 * gets alerts only if they turned them on here themselves. The browser has a
 * single push subscription, so it always belongs to whoever is signed in.
 *
 * Browser storage can be missing or blocked (private windows), so every
 * access is guarded; without it the worst case is the old behaviour: turn
 * alerts on again after signing in.
 */
const REMEMBER_KEY = 'whaletail.alertsAccounts';

function rememberedAccounts(): number[] {
  try {
    const v = JSON.parse(localStorage.getItem(REMEMBER_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is number => Number.isInteger(x)) : [];
  } catch {
    return [];
  }
}

/** Record whether `accountId` wants alerts on this device. */
export function rememberAlerts(accountId: number, on: boolean): void {
  try {
    const set = new Set(rememberedAccounts());
    if (on) set.add(accountId);
    else set.delete(accountId);
    localStorage.setItem(REMEMBER_KEY, JSON.stringify([...set]));
  } catch {
    /* no storage: alerts just will not resume by themselves after sign-in */
  }
}

/**
 * Whether alerts are on for `accountId` on this device: the browser has a
 * subscription AND this account turned alerts on here. The subscription alone
 * is not enough — it may be kept for another account that uses this device.
 */
export async function isOnForAccount(accountId: number): Promise<boolean> {
  return rememberedAccounts().includes(accountId) && (await currentSubscription()) !== null;
}

/**
 * What to do with this browser's alerts when `accountId` signs in. Pure, so
 * it can be tested without a browser: resume only for an account that turned
 * them on here, and only while notifications are still allowed.
 */
export function restoreDecision(remembered: readonly number[], accountId: number, support: PushSupport): 'resume' | 'leave' {
  return remembered.includes(accountId) && support === 'granted' ? 'resume' : 'leave';
}

/**
 * Called after sign-in (and on each page load while signed in, which also
 * repairs a device the server dropped). Re-registers this browser's push
 * subscription for the account, making a new one if the browser discarded it
 * — no permission prompt, since permission is already granted. Never throws.
 */
export async function resumeAfterSignIn(accountId: number, vapidPublicKey: string | null): Promise<void> {
  try {
    if (!vapidPublicKey) return;
    const reg = await registration();
    if (!reg) return;
    // One-time move from before alerts were remembered per account: back then
    // signing out always dropped the browser's subscription, so one that
    // exists now with nothing remembered belongs to whoever is signed in.
    if (rememberedAccounts().length === 0 && pushSupport() === 'granted' && (await reg.pushManager.getSubscription())) {
      rememberAlerts(accountId, true);
    }
    if (restoreDecision(rememberedAccounts(), accountId, pushSupport()) !== 'resume') return;
    const key = base64UrlToBytes(vapidPublicKey);
    let sub = await reg.pushManager.getSubscription();
    if (sub && !sameBytes(sub.options.applicationServerKey, key)) {
      await sub.unsubscribe();
      sub = null;
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return;
    await subscribePush({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, label: deviceLabel() });
  } catch {
    /* best effort: the Settings tab still offers "Turn on here" */
  }
}

/**
 * Sign-out: the server stops sending to this device, but the browser keeps its
 * subscription and the remembered account, so signing back in resumes alerts.
 * Must run while the session still exists. Never throws.
 */
export async function pauseOnSignOut(): Promise<void> {
  try {
    const sub = await currentSubscription();
    if (sub) await unsubscribePush(sub.endpoint).catch(() => undefined);
  } catch {
    /* best effort: signing out must not fail because of this */
  }
}

/**
 * "Turn off here" for `accountId`: forget its choice and tell the server. The
 * browser's own subscription is dropped too, unless another account on this
 * device still has alerts on (it resumes when they sign in). Never throws.
 */
export async function disableOnThisDevice(accountId: number): Promise<void> {
  rememberAlerts(accountId, false);
  try {
    const sub = await currentSubscription();
    if (!sub) return;
    await unsubscribePush(sub.endpoint).catch(() => undefined);
    if (rememberedAccounts().length === 0) await sub.unsubscribe();
  } catch {
    /* best effort */
  }
}
